import { memo } from 'react';
import { twJoin } from 'tailwind-merge';

import { formatPublishedAt } from './formatPublishedAt';

import { Badge, BadgeText } from '@/components/ui/badge';
import { BulletDot } from '@/components/ui/bullet-dot';
import { HStack } from '@/components/ui/hstack';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import type { AskAvailableResult } from '@/services/ask/api';

/** Past this many, the card names the count rather than listing every title. */
const MAX_LISTED_OTHER_POSTS = 2;

type RelatedInquiryCardProps = {
  result: AskAvailableResult;
  position: number;
  isRepeat: boolean;
  isSelected: boolean;
  onSelect: () => void;
};

/**
 * One ranked result, drawn from its published Inquiry Library posts. The lead
 * post's title is the headline; a question several posts cover lists the
 * others beneath the rationale, so the reader sees the spread before opening.
 *
 * Only posts reach this card. The unreviewed thread behind the question is
 * never shown, and the API no longer sends it.
 *
 * Selection is carried by a "Viewing" label and the accessibility state as
 * well as the border, so it does not rest on color. "Shown above" marks a
 * result an earlier turn already returned, so a follow-up that surfaces the
 * same answers reads as confirmation, not news.
 */
export const RelatedInquiryCard = memo(function RelatedInquiryCard({
  result,
  position,
  isRepeat,
  isSelected,
  onSelect,
}: RelatedInquiryCardProps) {
  const [leadPost, ...otherPosts] = result.posts;

  const publishedAt = formatPublishedAt(leadPost?.publishedAt);

  const listedOtherPosts = otherPosts.slice(0, MAX_LISTED_OTHER_POSTS);

  const unlistedCount = otherPosts.length - listedOtherPosts.length;

  const headline = leadPost?.title ?? '';

  return (
    <Pressable
      onPress={onSelect}
      accessibilityRole="button"
      accessibilityLabel={`Result ${position + 1}${isRepeat ? ', shown above' : ''}: ${headline}${
        otherPosts.length > 0 ? `, and ${otherPosts.length} more` : ''
      }`}
      accessibilityHint="Opens the inquiry from the Inquiry Library"
      accessibilityState={{ selected: isSelected }}
      className={twJoin(
        'w-full rounded-xl border bg-background-0 p-4 web:cursor-pointer web:transition-all web:duration-150 web:hover:bg-background-50',
        isSelected ? 'border-2 border-primary-500' : 'border-outline-500'
      )}>
      <VStack className="gap-2">
        <HStack className="flex-wrap items-center gap-2">
          {result.cancerType ? (
            <Badge action="info" size="md">
              <BadgeText className="text-info-700">{result.cancerType}</BadgeText>
            </Badge>
          ) : null}

          {publishedAt ? (
            <Text className="text-xs text-typography-700">Published {publishedAt}</Text>
          ) : null}

          {isRepeat ? (
            <Text className="font-semibold text-xs text-typography-700">Shown above</Text>
          ) : null}

          {isSelected ? (
            <Text className="ml-auto font-semibold text-xs text-primary-600">Viewing</Text>
          ) : null}
        </HStack>

        <Text className="font-medium text-base text-typography-900" numberOfLines={3}>
          {headline}
        </Text>

        {result.rationale ? (
          <Text className="text-sm text-typography-700" numberOfLines={3}>
            {result.rationale}
          </Text>
        ) : null}

        {otherPosts.length > 0 ? (
          <VStack className="gap-1 border-t border-outline-100 pt-2">
            <Text className="font-semibold text-xs text-typography-700">
              {otherPosts.length === 1
                ? '1 more inquiry on this question'
                : `${otherPosts.length} more inquiries on this question`}
            </Text>

            {listedOtherPosts.map((post) => (
              <HStack key={post.id} className="items-start gap-2">
                <BulletDot />
                <Text className="flex-1 text-sm text-typography-900" numberOfLines={1}>
                  {post.title}
                </Text>
              </HStack>
            ))}

            {unlistedCount > 0 ? (
              <Text className="text-xs text-typography-700">and {unlistedCount} more</Text>
            ) : null}
          </VStack>
        ) : null}
      </VStack>
    </Pressable>
  );
});
